import { createReducer } from '@reduxjs/toolkit';
import { CompanyDetailsDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';
import { clearBaseEntityState, IBaseEntityState, SaveEntityStatus } from './common';
import {
    setCompanyDetails,
    createNewDocumentCompanyDetail,
    createNewDocumentReceiverTemplate,
    clearCompanyDetails,
    removeDocumentCompanyDetail,
    removeDocumentReceiverTemplate,
    updateCompanyName,
    updateCurrency,
    updateLabelForDocumentCompanyDetail,
    updateValueForDocumentCompanyDetail,
    updateLabelForDocumentReceiverTemplate,
    updateValueForDocumentReceiverTemplate,
    saveCompanyDetails,
} from '../actions/companyDetailsActions';

export type DocCompType = { index: number; label: string; value: string };

export interface CompanyDetailsReducerState extends IBaseEntityState {
    companyDetails: CompanyDetailsDto | null;

    docCompDetails: DocCompType[] | null;
    docRecTemplate: DocCompType[] | null;
}

function clearState(state: CompanyDetailsReducerState): void {
    state.companyDetails = null;
    state.docCompDetails = null;
    state.docRecTemplate = null;

    clearBaseEntityState(state);
}

function getInitialState(): CompanyDetailsReducerState {
    const state: CompanyDetailsReducerState = {
        companyDetails: null,
        docCompDetails: null,
        docRecTemplate: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

export const companyDetailsReducer = createReducer<CompanyDetailsReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(setCompanyDetails.fulfilled, (state: CompanyDetailsReducerState, action: any) => {
                const compDetails: CompanyDetailsDto = action.payload;

                if (!compDetails) {
                    state.companyDetails === new CompanyDetailsDto();
                } else {
                    state.companyDetails = compDetails;

                    if (!!compDetails?.documentCompanyDetails) {
                        const newDocCompDetails: DocCompType[] = [];

                        Object.keys(compDetails.documentCompanyDetails).map((key) => {
                            const val = compDetails.documentCompanyDetails[key];
                            let index = 1;

                            if (newDocCompDetails.length > 0) {
                                index = newDocCompDetails[newDocCompDetails.length - 1].index + 1;
                            }

                            const newDocCompDetail: DocCompType = {
                                index: index,
                                label: key,
                                value: val,
                            };

                            newDocCompDetails.push(newDocCompDetail);
                        });

                        state.docCompDetails = newDocCompDetails;
                    }

                    if (!!compDetails?.documentReceiverTemplate) {
                        const newDocRecTemplate: DocCompType[] = [];

                        Object.keys(compDetails.documentReceiverTemplate).map((key) => {
                            const val = compDetails.documentReceiverTemplate[key];
                            let index = 1;

                            if (newDocRecTemplate.length > 0) {
                                index = newDocRecTemplate[newDocRecTemplate.length - 1].index + 1;
                            }

                            const newTemplate: DocCompType = {
                                index: index,
                                label: key,
                                value: val,
                            };

                            newDocRecTemplate.push(newTemplate);
                        });

                        state.docRecTemplate = newDocRecTemplate;
                    }
                }
            })
            .addCase(createNewDocumentCompanyDetail, (state: CompanyDetailsReducerState, action: any) => {
                const index =
                    !!state.docCompDetails && state.docCompDetails.length > 0
                        ? state.docCompDetails[state.docCompDetails.length - 1].index + 1
                        : 1;

                const newDocCompDetail: DocCompType = {
                    index: index,
                    label: '',
                    value: '',
                };

                if (!state.docCompDetails) {
                    state.docCompDetails = [newDocCompDetail];
                } else {
                    state.docCompDetails = [...state.docCompDetails, newDocCompDetail];
                }
            })
            .addCase(createNewDocumentReceiverTemplate, (state: CompanyDetailsReducerState, action: any) => {
                const index =
                    !!state.docRecTemplate && state.docRecTemplate.length > 0
                        ? state.docRecTemplate[state.docRecTemplate.length - 1].index + 1
                        : 1;

                const newDocRecTemp: DocCompType = {
                    index: index,
                    label: '',
                    value: '',
                };

                if (!state.docRecTemplate) {
                    state.docRecTemplate = [newDocRecTemp];
                } else {
                    state.docRecTemplate = [...state.docRecTemplate, newDocRecTemp];
                }
            })
            .addCase(clearCompanyDetails, (state: CompanyDetailsReducerState, action: any) => {
                clearState(state);
            })
            .addCase(removeDocumentCompanyDetail, (state: CompanyDetailsReducerState, action: any) => {
                if (!!state.docCompDetails) {
                    const index = action.payload;
                    if (state.docCompDetails.length === 0) {
                        state.docCompDetails = [];
                    } else {
                        const detailIndex = state.docCompDetails.findIndex((g) => g.index === index);
                        if (detailIndex > -1) {
                            state.docCompDetails.splice(detailIndex, 1);
                        }
                    }
                }
            })
            .addCase(removeDocumentReceiverTemplate, (state: CompanyDetailsReducerState, action: any) => {
                if (!!state.docRecTemplate) {
                    const index = action.payload;
                    if (state.docRecTemplate.length === 0) {
                        state.docCompDetails = [];
                    } else {
                        const templateIndex = state.docRecTemplate.findIndex((g) => g.index === index);
                        if (templateIndex > -1) {
                            state.docRecTemplate.splice(templateIndex, 1);
                        }
                    }
                }
            })
            .addCase(updateCompanyName, (state: CompanyDetailsReducerState, action: any) => {
                myAssert(() => state.companyDetails !== null, 'There needs to be goods if it is being updated');

                state.companyDetails = new CompanyDetailsDto({
                    ...(state.companyDetails ?? new CompanyDetailsDto()),
                    name: action.payload,
                });
            })
            .addCase(updateCurrency, (state: CompanyDetailsReducerState, action: any) => {
                myAssert(() => state.companyDetails !== null, 'There needs to be goods if it is being updated');

                state.companyDetails = new CompanyDetailsDto({
                    ...(state.companyDetails ?? new CompanyDetailsDto()),
                    currency: action.payload,
                });
            })
            .addCase(updateLabelForDocumentCompanyDetail, (state: CompanyDetailsReducerState, action: any) => {
                myAssert(() => state.docCompDetails !== null, 'There needs to be goods if it is being updated');

                const { index, label } = action.payload;

                const newDocCompDetailIndex = state.docCompDetails?.findIndex((dcd) => dcd.index === index);

                if (newDocCompDetailIndex !== undefined && newDocCompDetailIndex > -1 && !!state.docCompDetails) {
                    const newDocCompDetail = state.docCompDetails[newDocCompDetailIndex];

                    newDocCompDetail.label = label;

                    state.docCompDetails = [
                        ...state.docCompDetails.slice(0, newDocCompDetailIndex),
                        newDocCompDetail,
                        ...state.docCompDetails.slice(newDocCompDetailIndex + 1),
                    ];
                }
            })
            .addCase(updateValueForDocumentCompanyDetail, (state: CompanyDetailsReducerState, action: any) => {
                myAssert(() => state.docCompDetails !== null, 'There needs to be goods if it is being updated');

                const { index, value } = action.payload;

                const newDocCompDetailIndex = state.docCompDetails?.findIndex((dcd) => dcd.index === index);

                if (newDocCompDetailIndex !== undefined && newDocCompDetailIndex > -1 && !!state.docCompDetails) {
                    const newDocCompDetail = state.docCompDetails[newDocCompDetailIndex];

                    newDocCompDetail.value = value;

                    state.docCompDetails = [
                        ...state.docCompDetails.slice(0, newDocCompDetailIndex),
                        newDocCompDetail,
                        ...state.docCompDetails.slice(newDocCompDetailIndex + 1),
                    ];
                }
            })
            .addCase(updateLabelForDocumentReceiverTemplate, (state: CompanyDetailsReducerState, action: any) => {
                myAssert(() => state.docRecTemplate !== null, 'There needs to be goods if it is being updated');

                const { index, label } = action.payload;

                const newDocRecTempIndex = state.docRecTemplate?.findIndex((dcd) => dcd.index === index);

                if (newDocRecTempIndex !== undefined && newDocRecTempIndex > -1 && !!state.docRecTemplate) {
                    const newDocRecTemp = state.docRecTemplate[newDocRecTempIndex];

                    newDocRecTemp.label = label;

                    state.docRecTemplate = [
                        ...state.docRecTemplate.slice(0, newDocRecTempIndex),
                        newDocRecTemp,
                        ...state.docRecTemplate.slice(newDocRecTempIndex + 1),
                    ];
                }
            })
            .addCase(updateValueForDocumentReceiverTemplate, (state: CompanyDetailsReducerState, action: any) => {
                myAssert(() => state.docRecTemplate !== null, 'There needs to be goods if it is being updated');

                const { index, value } = action.payload;

                const newDocRecTempIndex = state.docRecTemplate?.findIndex((dcd) => dcd.index === index);

                if (newDocRecTempIndex !== undefined && newDocRecTempIndex > -1 && !!state.docRecTemplate) {
                    const newDocRecTemp = state.docRecTemplate[newDocRecTempIndex];

                    newDocRecTemp.value = value;

                    state.docRecTemplate = [
                        ...state.docRecTemplate.slice(0, newDocRecTempIndex),
                        newDocRecTemp,
                        ...state.docRecTemplate.slice(newDocRecTempIndex + 1),
                    ];
                }
            })
            .addCase(saveCompanyDetails.fulfilled, (state: CompanyDetailsReducerState, action: any) => {
                const compDetails: CompanyDetailsDto = action.payload;

                if (!compDetails) {
                    state.companyDetails === new CompanyDetailsDto();
                } else {
                    state.companyDetails = compDetails;

                    if (!!compDetails?.documentCompanyDetails) {
                        const newDocCompDetails: DocCompType[] = [];

                        Object.keys(compDetails.documentCompanyDetails).map((key) => {
                            const val = compDetails.documentCompanyDetails[key];
                            let index = 1;

                            if (newDocCompDetails.length > 0) {
                                index = newDocCompDetails[newDocCompDetails.length - 1].index + 1;
                            }

                            const newDocCompDetail: DocCompType = {
                                index: index,
                                label: key,
                                value: val,
                            };

                            newDocCompDetails.push(newDocCompDetail);
                        });

                        state.docCompDetails = newDocCompDetails;
                    }

                    if (!!compDetails?.documentReceiverTemplate) {
                        const newDocRecTemplate: DocCompType[] = [];

                        Object.keys(compDetails.documentReceiverTemplate).map((key) => {
                            const val = compDetails.documentReceiverTemplate[key];
                            let index = 1;

                            if (newDocRecTemplate.length > 0) {
                                index = newDocRecTemplate[newDocRecTemplate.length - 1].index + 1;
                            }

                            const newTemplate: DocCompType = {
                                index: index,
                                label: key,
                                value: val,
                            };

                            newDocRecTemplate.push(newTemplate);
                        });

                        state.docRecTemplate = newDocRecTemplate;
                    }
                }

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(saveCompanyDetails.pending, (state: CompanyDetailsReducerState, action: any) => {
                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveCompanyDetails.rejected, (state: CompanyDetailsReducerState, action: any) => {
                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
